import { type Band, bandFor } from "../types.js";
import {
  type Au08Input,
  Au08InputSchema,
  type Au08Output,
  type Au08RuleSet,
  type LhcResult,
  type MlsResult,
  type SpouseCoverSituation,
} from "./types.js";

/** Shift every band boundary up by `increment` (used for the per-child
 * family threshold adjustment). */
function shiftBands(bands: readonly Band[], increment: number): Band[] {
  if (increment === 0) return [...bands];
  return bands.map((b) => ({
    from: b.from === 0 ? 0 : b.from + increment,
    to: b.to === null ? null : b.to + increment,
    rate: b.rate,
  }));
}

/**
 * Classify the household's hospital-cover picture. MLS is per-individual but
 * the ATO only exempts a person when an appropriate policy covers them, their
 * spouse AND all dependants, so we surface the whole household for
 * information-only copy. Single filers are always `"single"` (a partner's
 * cover is irrelevant to a single's own liability).
 */
function spouseCoverFor(input: Au08Input): SpouseCoverSituation {
  if (input.filingStatus !== "family") return "single";
  const user = input.holdsHospitalCover;
  const partner = input.partnerHoldsHospitalCover;
  if (user && partner) return "all_covered";
  if (user && !partner) return "user_only";
  if (!user && partner) return "partner_only";
  return "none_covered";
}

function calcMls(input: Au08Input, rules: Au08RuleSet): MlsResult {
  const spouseCover = spouseCoverFor(input);
  // The household is exposed through the partner when the user is personally
  // covered (so the user owes nothing) but the partner is not. The user's own
  // surcharge is still 0; this only flags family-wide exposure for copy.
  const householdExposedViaPartner = spouseCover === "user_only";

  // Holding eligible hospital cover exempts the USER from MLS on their own
  // income. (Each person's MLS is assessed individually — a covered user owes
  // nothing even if their partner lacks cover; the partner's own exposure is
  // surfaced via `householdExposedViaPartner`, not levied here.)
  if (input.holdsHospitalCover) {
    return {
      amount: 0,
      rate: 0,
      testedIncome: input.income,
      leviedIncome: input.income,
      exemptViaCover: true,
      spouseCover,
      householdExposedViaPartner,
    };
  }

  let bands: readonly Band[];
  let testedIncome: number;
  if (input.filingStatus === "family") {
    // Per-child increment applies after the first child.
    const extraChildren = Math.max(0, input.children - 1);
    const increment = extraChildren * rules.mls.familyChildIncrement;
    bands = shiftBands(rules.mls.family, increment);
    // Tier is set by COMBINED income for couples/families (ATO M2 "Alice and
    // Adam" worked example: $150k + $89k = $239k combined → family Tier 2).
    testedIncome = input.income + input.partnerIncome;
  } else {
    bands = rules.mls.single;
    testedIncome = input.income;
  }

  const band = bandFor(bands, testedIncome);
  // The tier (rate) is set by tested income, but the surcharge is always
  // levied on the user's own income for MLS purposes.
  const amount = Math.round(input.income * band.rate);
  return {
    amount,
    rate: band.rate,
    testedIncome,
    leviedIncome: input.income,
    exemptViaCover: false,
    spouseCover,
    householdExposedViaPartner,
  };
}

function calcLhc(input: Au08Input, rules: Au08RuleSet): LhcResult {
  const { graceAge, loadingPerYear, cap, dropoffYears } = rules.lhc;

  // Base day is 1 July following the 31st birthday; nobody at or under the
  // grace age can carry a loading yet.
  if (input.age <= graceAge) {
    return { status: "not_yet_applicable", loading: 0, yearsRemaining: null };
  }

  const loadingForAge = (ageFirstHeld: number): number => {
    if (ageFirstHeld <= graceAge) return 0;
    return Math.min(cap, loadingPerYear * (ageFirstHeld - 30));
  };

  if (!input.holdsHospitalCover) {
    // No cover and past the base day → quote the loading they would incur
    // if they took out cover now.
    const loading = loadingForAge(input.age);
    return {
      status: loading === 0 ? "no_loading" : "would_apply_if_taken_now",
      loading,
      yearsRemaining: null,
    };
  }

  // Holds cover but can't recall when they started → can't compute.
  if (input.firstHeldYear === "unsure" || input.firstHeldYear === null) {
    return { status: "indeterminate", loading: 0, yearsRemaining: null };
  }

  const yearsHeld = rules.assessmentYear - input.firstHeldYear;
  const ageFirstHeld = input.age - yearsHeld;

  // Loading drops off after `dropoffYears` of continuous cover.
  if (yearsHeld >= dropoffYears) {
    return { status: "no_loading", loading: 0, yearsRemaining: 0 };
  }

  const loading = loadingForAge(ageFirstHeld);
  if (loading === 0) {
    return { status: "no_loading", loading: 0, yearsRemaining: null };
  }
  return {
    status: "applies",
    loading,
    yearsRemaining: Math.max(0, dropoffYears - yearsHeld),
  };
}

/**
 * Calculate AU-08 (MLS + LHC) for a single assessment. Pure: same inputs +
 * ruleset always yield the same output. Validates the input at the boundary
 * so malformed payloads fail loudly rather than producing junk numbers.
 */
export function calculateAu08(
  rawInput: Au08Input,
  rules: Au08RuleSet,
): Au08Output {
  const input = Au08InputSchema.parse(rawInput);
  return {
    mls: calcMls(input, rules),
    lhc: calcLhc(input, rules),
  };
}
